import { useEffect, useState } from 'react'
import { getConfig } from '~/services/api/configService'
import type { AvatarDetails } from '~/types/avatar'
import type { EmoteDetails } from '~/types/emote'
import { getErrorMessage } from '~/utils/errorMessage'
import { AppConfigContext, type AppConfigContextValue, type DefaultAvatarInfo } from './context'

// デフォルトアバターの所有者ID（スクランブル解除用）
// システムデフォルトアバターは常に "system" が所有者
const DEFAULT_AVATAR_OWNER_ID = 'system'

// useState 初期値用の定数（参照同一性のため）
const INITIAL_DEFAULT_EMOTES: EmoteDetails[] = []

interface Props {
  children: React.ReactNode
}

export function AppConfigProvider({ children }: Props) {
  const [defaultAvatar, setDefaultAvatar] = useState<DefaultAvatarInfo | null>(null)
  const [rawDefaultAvatar, setRawDefaultAvatar] = useState<AvatarDetails | null>(null)
  const [defaultEmotes, setDefaultEmotes] = useState<EmoteDetails[]>(INITIAL_DEFAULT_EMOTES)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const config = await getConfig()

        if (!config.defaultAvatar.vrmUrl) {
          throw new Error('デフォルトアバターのURLが取得できませんでした')
        }

        setRawDefaultAvatar(config.defaultAvatar)
        setDefaultAvatar({
          vrmUrl: config.defaultAvatar.vrmUrl,
          ownerUserId: DEFAULT_AVATAR_OWNER_ID,
          version: config.defaultAvatar.version,
        })
        setDefaultEmotes(config.defaultEmotes)
        setError(null)
      } catch (err) {
        console.error('アプリケーション設定の取得に失敗しました:', err)
        setError(getErrorMessage(err, 'アプリケーション設定の取得に失敗しました'))
      } finally {
        setIsLoading(false)
      }
    }

    loadConfig()
  }, [])

  const value: AppConfigContextValue = {
    defaultAvatar,
    isLoading,
    error,
    rawDefaultAvatar,
    defaultEmotes,
  }

  return <AppConfigContext.Provider value={value}>{children}</AppConfigContext.Provider>
}

export type { AppConfigContextValue, DefaultAvatarInfo } from './context'
export { useAppConfig } from './hooks'
